








import Vue from "vue";
import coreApiClient from "@/services/apis/coreApiClient";
import userManager from "@/services/userManager";
import makeSelectOptions from "@common/util/makeSelectOptions";
import { issueStatuses, issueTypes as defaultIssueTypes } from "@/constants/issues";
import BlurredTenant from '@/components/BlurredTenant.vue';

export default Vue.extend({
  components: { BlurredTenant },
  data() {
    return {
      showBlur: userManager.checkRole(["cpoAdmin", "cpoMonitor"], true) && !!userManager.getUserInfo().tenant?.setting?.expDate && userManager.getUserInfo().tenant.setting.expDate < new Date().getTime(),
      issueTypes: defaultIssueTypes,
      issueStatuses,
      forceRecompute: 0, // Trigger for computed properties
    };
  },
  computed: {
    issueTypeItems() {
      return makeSelectOptions(this.issueTypes);
    },
    tableOptions() {
      const vm = this; // Capture Vue instance
      // Add dependency to force recompute
      this.forceRecompute;
      return {
        attrs: {
          "item-key": "_id",
          "sort-by": "createdTime",
          "sort-desc": true,
        },
        content: {
          name: "Issues",
          urlEnabled: true,
          search: {
            ext: {
              hidden: true,
            },
          },
          filters: {
            filters: {
              tenantId: userManager.checkRole(["systemAdmin", "admin"]) && {
                type: "XAutocomplete",
                attrs: {
                  label: "Organization",
                  required: true,
                  "item-value": "_id",
                  "item-text": "name",
                  xOptions: {
                    content: {
                      async itemsSuggestor(search, value, limit) {
                        const { items } = await coreApiClient.call("tenants", "findAll", {
                          payload: JSON.stringify({
                            limit,
                            search,
                            orderBy: "name",
                            orderType: "asc",
                            filters: [
                              {
                                key: "_id",
                                operator: "equal_to",
                                value,
                              },
                            ],
                          }),
                        });
                        return items;
                      },
                    },
                  },
                },
                rules: {
                  equal_to: {},
                },
              },
              createdByUserId: userManager.checkRole(["systemAdmin", "admin", "cpoAdmin", "cpoMonitor"]) && {
                type: "XAutocomplete",
                attrs: {
                  label: "User",
                  required: true,
                  "item-value": "_id",
                  "item-text": "email",
                  xOptions: {
                    content: {
                      async itemsSuggestor(search, value, limit) {
                        const { items } = await coreApiClient.call("users", "findAll", {
                          payload: JSON.stringify({
                            limit,
                            search,
                            orderBy: "email",
                            orderType: "asc",
                            filters: [
                              {
                                key: "_id",
                                operator: "equal_to",
                                value,
                              },
                            ],
                          }),
                        });
                        return items;
                      },
                    },
                  },
                },
                rules: {
                  equal_to: {},
                },
              },
              type: {
                type: "select",
                attrs: {
                  label: "Issue Type",
                  items: (() => {
                    // Add explicit dependency to trigger reactivity
                    vm.forceRecompute;
                    // Force dereference Observer to plain object
                    const issueTypes = {...vm.issueTypes};
                    console.log('items IIFE called, issueTypes:', issueTypes);
                    return makeSelectOptions(issueTypes);
                  })(),
                },
                rules: {
                  equal_to: {},
                },
              },
              status: {
                type: "select",
                attrs: {
                  label: "Status",
                  items: makeSelectOptions(issueStatuses),
                },
                rules: {
                  equal_to: {},
                },
              },
              subject: {
                attrs: {
                  label: "Subject",
                },
                rules: {
                  match: {},
                },
              },
            },
          },
          displayFields: {
            type: {
              text: "Type",
              sortable: true,
              options: {
                label: true,
                transform: (value) => {
                  const issueTypes = {...vm.issueTypes};
                  return issueTypes[value] || value;
                },
              },
            },
            subject: {
              text: "Subject",
              sortable: true,
            },
            status: {
              text: "Status",
              sortable: true,
              options: {
                label: true,
                transform: (value) => issueStatuses[value] || value,
              },
            },
            attachments: {
              text: "Attachments",
              sortable: false,
              options: {
                transform: (items) => {
                  return items.map((item, idx) => {
                    return `<a href="${item}" target="_blank">File ${idx + 1}</a>`
                  }).join(', ');
                },
              },
            },
            user: userManager.checkRole(["systemAdmin", "admin", "cpoAdmin", "cpoMonitor"]) && {
              text: "Created By",
              options: {
                subProp: "createdByUser.email",
                label: true,
                sortBy: "userId",
              },
            },
            tenant: {
              text: "Tenant",
              options: {
                subProp: "tenant.name",
                label: true,
                sortBy: "tenantId",
              },
            },
            action: {
              text: "Actions",
            },
          },
          topActionButtons: {
            insert: false,
          },
          template: {
            itemActionButtons: {
              view: {
                target: {
                  dialog: {
                    ext: {
                      subTitleMaker: "subject",
                    },
                  },
                },
              },
              edit: userManager.checkRole(["admin", "cpoAdmin"]) && {
                target: {
                  dialog: {
                    title: "Update Issue Status",
                    ext: {
                      subTitleMaker: "subject",
                    },
                  },
                },
              },
            },
            components: {
              
            }
          },
        },
        ext: {
            dataProvider: {
              async findAll(options) {
                // Check if user is admin to use admin endpoint or user endpoint
                if (userManager.checkRole(["systemAdmin", "admin", "cpoAdmin", "cpoMonitor"])) {
                  const { items, count } = await coreApiClient.call("issues", "getAllIssues", options);
                  return [items, count];
                } else {
                  const { items, count } = await coreApiClient.call("issues", "getMyIssues", options);
                  return [items, count];
                }
              },
              async insert(item) {
                return await coreApiClient.call("issues", "create", undefined, item);
              },
              async update(item) {
                return await coreApiClient.call(
                  "issues",
                  "updateIssueStatus",
                  {
                    id: item._id,
                  },
                  {
                    status: item.status,
                  }
                );
              },
            },
          default: {
            defaultInputs: {
              status: {
                type: "select",
                attrs: {
                  label: "Status",
                  required: true,
                  items: makeSelectOptions(issueStatuses),
                },
              },
            },
            editForm: userManager.checkRole(["admin", "cpoAdmin"]) && {
              content: {
                fieldNames: ["status"],
              },
            },
            view: {
              content: {
                fieldNames: ["type", "subject", "status", "attachments", "createdTime", "updatedTime"],
              },
            },
          },
        },
      };
    }
  },
  async mounted() {
    await this.loadIssueTypes();
  },
  methods: {
    async loadIssueTypes() {
      try {
        const issueTypes = await coreApiClient.call("issues", "getIssueTypes");
        const issueTypeMap = {};
        issueTypes.forEach(({type, label}) => {
          issueTypeMap[type] = label;
        });
        console.log('issueTypeMap', issueTypeMap);
        this.$set(this, 'issueTypes', {...issueTypeMap});
        this.forceRecompute++; // Trigger computed property recompute
        console.log('issueTypes', this.issueTypes);
      } catch (error) {
        console.error("Failed to load issue types:", error);
        // Fallback to default types from constants
        this.$set(this, 'issueTypes', defaultIssueTypes);
      }
    },
  },
});
